import { House, MoreHorizontal, ArrowDownToLine, Check, Cloud, CloudOff, Keyboard, ListOrdered, OctagonX, RefreshCw, TriangleAlert, CircleCheck, X, ScanLine, Camera, HardDriveDownload } from "lucide-react";
import type { CalendarDate, Catalog, History, Verdict } from "@hitl/rotations-domain";
import type { SpotView } from "../state/session";
import type { SyncState } from "../state/eventLog";
import type { CameraStatus } from "./useQrScanner";
import { citeLine, formatDay, reasonLine, VERDICT_WORD, windowProgress } from "./copy";

/* ── Top bar ─────────────────────────────────────────────────────────────── */

interface StopGroup {
  readonly clientId: string;
  readonly spots: readonly SpotView[];
}

export function groupByClient(spots: readonly SpotView[]): StopGroup[] {
  const groups: StopGroup[] = [];
  for (const spot of spots) {
    const last = groups.at(-1);
    if (last && last.clientId === spot.clientId) groups[groups.length - 1] = { ...last, spots: [...last.spots, spot] };
    else groups.push({ clientId: spot.clientId, spots: [spot] });
  }
  return groups;
}

const SYNC_COPY: Record<SyncState, string> = {
  saved: "All saved",
  syncing: "Saving…",
  offline: "Waiting for signal",
  "storage-unavailable": "Not saved on phone",
};

export function SyncBadge({ state, pending }: { state: SyncState; pending: number }) {
  const Icon = state === "saved" ? Cloud : state === "syncing" ? RefreshCw : state === "offline" ? CloudOff : HardDriveDownload;
  const text = state === "offline" && pending > 0 ? `${pending} waiting for signal` : SYNC_COPY[state];
  return (
    <span className={`sync sync--${state}`} role="status">
      <Icon aria-hidden size={15} strokeWidth={2.25} className={state === "syncing" ? "spin" : undefined} />
      {text}
    </span>
  );
}

export function TopBar(props: {
  spots: readonly SpotView[];
  current: SpotView;
  syncState: SyncState;
  pending: number;
  dayDone?: boolean;
  onOpenRoute: () => void;
  onHome?: () => void;
}) {
  const { spots, current, syncState, pending, dayDone, onOpenRoute, onHome } = props;
  const groups = groupByClient(spots);
  const stopNumber = groups.findIndex((g) => g.clientId === current.clientId && g.spots.some((s) => s.index === current.index)) + 1;
  return (
    <header className="top">
      <div className="top__row">
        {onHome && (
          <button className="top__home" onClick={onHome} aria-label="Home">
            <House aria-hidden size={20} strokeWidth={2.25} />
          </button>
        )}
        <button className="top__where" onClick={onOpenRoute} aria-label={`Stop ${stopNumber} of ${groups.length}. Open today's route`}>
          <span className="top__stop">
            {dayDone ? `${spots.length} spots at ${groups.length} stops` : `Stop ${stopNumber} of ${groups.length} · ${current.spotLabel}`}
          </span>
          <span className="top__client">{dayDone ? "All stops done" : current.clientName}</span>
        </button>
        <SyncBadge state={syncState} pending={pending} />
      </div>
      <button className="progress" onClick={onOpenRoute} aria-label="Open today's route">
        {groups.map((g) => (
          <span key={`${g.clientId}-${g.spots[0]?.index}`} className="progress__stop">
            {g.spots.map((s) => (
              <span
                key={s.slotId}
                className={`progress__seg${s.done ? " is-done" : ""}${s.index === current.index ? " is-current" : ""}`}
              />
            ))}
          </span>
        ))}
      </button>
    </header>
  );
}

/* ── Verdict ─────────────────────────────────────────────────────────────── */

export const LIGHT_ICON = { green: CircleCheck, amber: TriangleAlert, red: OctagonX } as const;

export function VerdictPanel(props: {
  potId: string;
  verdict: Verdict;
  spot: SpotView;
  catalog: Catalog;
  history: History;
  today: CalendarDate;
}) {
  const { potId, verdict, spot, catalog, history, today } = props;
  const Icon = LIGHT_ICON[verdict.light];
  const ctx = { potId, clientName: spot.clientName, spotLabel: spot.spotLabel, catalog };
  const blocked = verdict.reasons.find((r) => r.code === "repeat-blocked");
  const cite = citeLine(verdict, potId, history, catalog);
  const lines =
    verdict.reasons.length > 0
      ? verdict.reasons.slice(0, 3).map((r) => reasonLine(r, ctx))
      : [`Never at ${spot.clientName} in the last 12 months.`];
  const offPlan = spot.plannedPotId && spot.plannedPotId !== potId && verdict.light !== "red";

  return (
    <div className={`verdict verdict--${verdict.light}`}>
      <span className="chip">
        <Icon aria-hidden size={22} strokeWidth={2.5} />
        {VERDICT_WORD[verdict.light]}
      </span>
      <div className="pot-number" aria-label={`Pot ${potId}`}>
        {potId}
      </div>
      <ul className="reasons">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {blocked && blocked.code === "repeat-blocked" && (
        <div className="window" aria-hidden>
          <div className="window__track">
            <div className="window__fill" style={{ width: `${windowProgress(blocked.lastPlacedOn, blocked.allowedFrom, today) * 100}%` }} />
            <div className="window__today" style={{ left: `${windowProgress(blocked.lastPlacedOn, blocked.allowedFrom, today) * 100}%` }} />
          </div>
          <div className="window__labels">
            <span>Placed {formatDay(blocked.lastPlacedOn)}</span>
            <span>Allowed {formatDay(blocked.allowedFrom)}</span>
          </div>
        </div>
      )}
      {cite && <p className="cite">{cite}</p>}
      {offPlan && (
        <p className="cite">
          Not the planned pot (<span className="inline-pot">{spot.plannedPotId}</span>). That's fine.
        </p>
      )}
    </div>
  );
}

export function SwapLine({ spot }: { spot: SpotView }) {
  if (!spot.takesAway) return <p className="swap">Empty spot: nothing to take away.</p>;
  return (
    <p className="swap">
      Take away <strong>{spot.takesAway}</strong>
      {spot.takenAwayGoesTo
        ? ` · it goes to ${spot.takenAwayGoesTo.clientName}, ${spot.takenAwayGoesTo.spotLabel}`
        : " · it comes home"}
    </p>
  );
}

export function WaitingPanel({ spot }: { spot: SpotView }) {
  return (
    <div className="waiting">
      <p className="waiting__lead">Scan the pot for {spot.spotLabel}</p>
      {spot.plannedPotId && (
        <p className="waiting__plan">
          Planned: <strong>{spot.plannedPotId}</strong>
        </p>
      )}
    </div>
  );
}

export function DonePanel({ spot }: { spot: SpotView }) {
  return (
    <div className="verdict verdict--done">
      <span className="chip">
        <Check aria-hidden size={22} strokeWidth={2.75} />
        Done
      </span>
      <div className="pot-number">{spot.done?.potId}</div>
      <ul className="reasons">
        <li>
          Placed at {spot.clientName}, {spot.spotLabel} today.
        </li>
      </ul>
    </div>
  );
}

/* ── Thumb rail ──────────────────────────────────────────────────────────── */

export function RailButton(props: {
  label: string;
  icon: typeof Check;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
}) {
  const { label, icon: Icon, onClick, primary, disabled } = props;
  return (
    <button className={`rail__btn${primary ? " rail__btn--primary" : ""}`} onClick={onClick} disabled={disabled}>
      <span className="rail__disc">
        <Icon aria-hidden size={primary ? 32 : 26} strokeWidth={2.5} />
      </span>
      <span className="rail__label">{label}</span>
    </button>
  );
}

export const RailIcons = { Check, X, Keyboard, ScanLine, ListOrdered, ArrowDownToLine, MoreHorizontal };

/* ── Camera states ───────────────────────────────────────────────────────── */

export function CameraNotice(props: { status: CameraStatus; onStart: () => void; onType: () => void }) {
  const { status, onStart, onType } = props;
  if (status === "live" || status === "starting") return null;
  const copy: Record<Exclude<CameraStatus, "live" | "starting">, { title: string; body: string }> = {
    "needs-permission": {
      title: "Turn on the camera to scan stickers",
      body: "Rotations reads the QR sticker on each pot. The picture stays on this phone.",
    },
    denied: {
      title: "The camera is blocked",
      body: "Allow it in Settings › Safari › Camera, or type the pot number instead.",
    },
    unavailable: { title: "No camera on this device", body: "Type the pot number instead." },
    error: { title: "The camera didn't start", body: "Try again, or type the pot number instead." },
  };
  const { title, body } = copy[status];
  return (
    <div className="notice">
      <Camera aria-hidden size={28} strokeWidth={2} />
      <p className="notice__title">{title}</p>
      <p className="notice__body">{body}</p>
      <div className="notice__actions">
        {(status === "needs-permission" || status === "error") && (
          <button className="btn btn--light" onClick={onStart}>
            Turn on camera
          </button>
        )}
        <button className="btn btn--ghost" onClick={onType}>
          Type the number
        </button>
      </div>
    </div>
  );
}
